import React, {useCallback, useEffect, useRef, useState} from 'react'
import {Navigate, Outlet} from 'react-router-dom'
import {createEditorDevTools, createEditorEngine, intentMap, serialize} from 'editor-core'
import {createPersistentAdapter} from './custom-shape/persistence/createPersistentAdapter'
import {RemotePersistentService} from './custom-shape/persistence/RemotePersistentService'
import DraftPersistentService from './custom-shape/persistence/DraftPersistentService'
import DraftPreviewStore from './custom-shape/persistence/DraftPreviewStore'
import DraftImageStore from './custom-shape/persistence/DraftImageStore'
import {STORAGE_KEYS} from './custom-shape/constants/storageKeys'
import {createEditorRuntime} from './custom-shape/createEditorRuntime'
import {updateNode, setZoom} from './custom-shape/editorActions'
import {ImagePersistenceService} from './custom-shape/integrations/design-library/ImagePersistenceService'
import EditorCanvas from './custom-shape/ui/components/EditorCanvas'
import Sidebar from './custom-shape/ui/components/sidebar/Sidebar'
import RemoveBgModal from './custom-shape/ui/components/sidebar/RemoveBgModal'
import {useRemoveBgApply} from './custom-shape/ui/hooks/useRemoveBgApply'
import {backgroundRemovalService} from './custom-shape/ui/hooks/useBackgroundRemoval'
import ViewToggle from './custom-shape/ui/components/ViewToggle'
import SaveShapeModal from './custom-shape/ui/modals/SaveShapeModal'
import CanvasEmptyState from './custom-shape/ui/components/CanvasEmptyState'
import {EditorConfig, VIEW_MODES} from './custom-shape/constants/editorConfig'
import {CustomShapeUIConfig} from './custom-shape/constants/uiConfig'
import {ReactComponent as UndoIcon} from '../assets/icons/undo.svg'
import {ReactComponent as RedoIcon} from '../assets/icons/redo.svg'
import {ReactComponent as PalmIcon} from '../assets/icons/palm.svg'
import {ReactComponent as MinusIcon} from '../assets/icons/minus.svg'
import {ReactComponent as PlusIcon} from '../assets/icons/plus.svg'
import {ReactComponent as ChevronLeftIcon} from '../assets/icons/chevron-left.svg'
import {SpinnerIcon} from './common/icons'
import {getContentBounds} from './custom-shape/getContentBounds'
import {getLinksUploadArtwork, getListCustomShapeTemplate} from '../services/ArtworkTemplateServices'
import {uploadTempArtwork} from '../services/ArtworkTemplateServices'
import {useSearchParams, useNavigate} from 'react-router-dom'
import {useEditorRuntime, selectorDocument} from './custom-shape/ui/hooks/useEditorRuntime'
import {useElementSizeObserver} from '../hooks/useElementSizeObserver'
import {createKonvaRuntimeAdapter} from './custom-shape/canvas/konva/createKonvaRuntimeAdapter'
import {createKonvaInputAdapter} from './custom-shape/canvas/konva/createKonvaInputAdapter'
import {InteractionController} from './custom-shape/canvas/konva/interaction/InteractionController'
import {restoreDraftImages} from './custom-shape/ui/utils/restoreDraftImages'
import {toaster} from '../shared/PFToast'

const CUSTOM_SHAPE_EDIT_PAYLOAD_KEY = 'customShapeEditPayload'

const InteractionMode = EditorConfig.INTERACTION_MODES

const EXPORT_SILHOUETTE_OPTIONS = { threshold: 10, strokeColor: '#000000', pixelRatio: 3 }

const CURRENT_SESSION_DRAFT_ID = '_current'

const devTools = createEditorDevTools({maxLogSize: 100})

const editorEngine = createEditorEngine({
    initialState: {
        nodes: new Map(),
        selection: {
            nodeIds: [],
        },
        order: [],
        history: {
            past: [],
            future: [],
        },
        viewport: {
            scale: 1,
            x: 0,
            y: 0,
        },
    },
    intentMap,
    debug: true,
    logger: devTools.logger,
})

devTools.connect(editorEngine)

const CreateCustomShape = () => {
    const runtimeRef = useRef(null)

    if (!runtimeRef.current) {
        const interaction = new InteractionController()
        const canvasAdapter = createKonvaRuntimeAdapter({
            editorEngine,
            interaction,
            createInputAdapter: createKonvaInputAdapter,
        })

        const remotePersistentService = RemotePersistentService({
            getPreviewImage: async () => {
                const runtime = runtimeRef.current
                if (!runtime) return null
                const blob = await capturePreviewBlob(VIEW_MODES.SILHOUETTE)
                if (!blob) return null
                const {success, data} = await getLinksUploadArtwork({limit: 1})
                if (!success || !data?.[0]) throw new Error('Failed to get upload link')
                const presignedUrl = data[0]
                await uploadTempArtwork(presignedUrl, blob, 'image/png')
                return presignedUrl.split('?')[0]
            },
        })
        const persistentAdapter = createPersistentAdapter({
            persistentService: remotePersistentService,
        })

        runtimeRef.current = createEditorRuntime({
            editorEngine,
            canvasAdapter,
            persistentAdapter,
            devTools,
        })
    }
    const runtime = runtimeRef.current

    const {state, dispatch} = useEditorRuntime(runtime, selectorDocument)
    const [viewportScale, setViewportScale] = useState(() => runtime.getState().viewport?.scale ?? 1)
    const [viewMode, setViewMode] = useState(VIEW_MODES.OVERLAY)
    const [isSaving, setIsSaving] = useState(false)
    const [isNexting, setIsNexting] = useState(false)
    const [isSaveModalOpen, setIsSaveModalOpen] = useState(false)
    const [silhouettePreviewUrl, setSilhouettePreviewUrl] = useState(null)
    const [savedShapesRefreshKey, setSavedShapesRefreshKey] = useState(0)
    const [currentDraftId, setCurrentDraftId] = useState(null)
    const [fragmentCount, setFragmentCount] = useState(0)
    const [showRemoveBgModal, setShowRemoveBgModal] = useState(false)
    const [removeBgNode, setRemoveBgNode] = useState(null)

    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const editPayloadAppliedRef = useRef(false)

    const canvasWrapperRef = useRef(null)
    const canvasSize = useElementSizeObserver(canvasWrapperRef, {
        minWidth: EditorConfig.VIEWPORT.MIN_WIDTH,
        minHeight: EditorConfig.VIEWPORT.MIN_HEIGHT,
    })

    const interaction = runtime.interaction
    const [interactionMode, setInteractionMode] = useState(() => interaction.getMode())

    useEffect(() => {
        if (!interaction?.subscribe) return
        const unsubscribe = interaction.subscribe((nextMode) => {
            setInteractionMode(nextMode)
        })
        return () => unsubscribe?.()
    }, [interaction])

    const toggleMode = () => {
        interaction.setMode(interaction.is(InteractionMode.PAN) ? InteractionMode.IDLE : InteractionMode.PAN)
    }

    // --- Remove-bg orchestration ---
    const onOpenRemoveBg = useCallback((node) => {
        setRemoveBgNode(node)
        setShowRemoveBgModal(true)
    }, [])

    const {handleApply: handleRemoveBgApply} = useRemoveBgApply({runtime, node: removeBgNode})

    const handleRemoveBgOnApply = useCallback(
        (blob) => {
            handleRemoveBgApply(blob)
        },
        [handleRemoveBgApply],
    )

    useEffect(() => {
        backgroundRemovalService.warmup()
    }, [])

    useEffect(() => {
        const editFlag = searchParams.get('edit')
        if (!editFlag || editPayloadAppliedRef.current) return

        let raw
        try {
            raw = window.localStorage.getItem(CUSTOM_SHAPE_EDIT_PAYLOAD_KEY)
        } catch {
            return
        }
        if (!raw) return

        let payload
        try {
            payload = JSON.parse(raw)
        } catch {
            return
        }

        editPayloadAppliedRef.current = true

        const rt = runtimeRef.current
        if (!rt?.loadDocument) return

        const applyPayload = async () => {
            if (payload.source === 'template') {
                const doc =
                    payload.doc == null ? null : typeof payload.doc === 'string' ? JSON.parse(payload.doc) : payload.doc
                if (doc) {
                    await rt.loadDocument(doc, payload.meta || {})
                }
            } else if (payload.source === 'draft' && payload.draftId) {
                const entry = DraftPersistentService.loadOne(payload.draftId)
                if (entry?.document) {
                    await rt.loadDocument(entry.document)
                    await restoreDraftImages(rt, payload.draftId)
                    setCurrentDraftId(payload.draftId)
                }
            }

            try {
                window.localStorage.removeItem(CUSTOM_SHAPE_EDIT_PAYLOAD_KEY)
            } catch {
                // ignore
            }
        }

        applyPayload()
    }, [searchParams])

    useEffect(() => {
        if (!runtime?.subscribe) return
        const unsubscribe = runtime.subscribe((nextState) => {
            const scale = nextState.viewport?.scale ?? 1
            setViewportScale(scale)
        })
        return () => unsubscribe?.()
    }, [runtime])

    const handleZoomOut = () => {
        const {viewport} = runtime.getState()
        const scale = viewport?.scale ?? 1
        const minScale = EditorConfig.ZOOM.MIN_PERCENT / 100
        const stepScale = EditorConfig.ZOOM.STEP_PERCENT / 100
        const newScale = Math.max(minScale, scale - stepScale)
        const pointer = {x: canvasSize.width / 2, y: canvasSize.height / 2}
        const center = {
            x: pointer.x / scale + viewport.x,
            y: pointer.y / scale + viewport.y,
        }
        dispatch(setZoom({scale: newScale, center, pointer}))
    }

    const handleZoomIn = () => {
        const {viewport} = runtime.getState()
        const scale = viewport?.scale ?? 1
        const maxScale = EditorConfig.ZOOM.MAX_PERCENT / 100
        const stepScale = EditorConfig.ZOOM.STEP_PERCENT / 100
        const newScale = Math.min(maxScale, scale + stepScale)
        const pointer = {x: canvasSize.width / 2, y: canvasSize.height / 2}
        const center = {
            x: pointer.x / scale + viewport.x,
            y: pointer.y / scale + viewport.y,
        }
        dispatch(setZoom({scale: newScale, center, pointer}))
    }

    const hasNodes =
        (state?.order && state.order.length > 0) ||
        (state?.nodes && typeof state.nodes.size === 'number' && state.nodes.size > 0)

    const canProceedNext = hasNodes && fragmentCount < 2
    const isNextDisabled = isSaving || isNexting || !canProceedNext
    const isSaveDisabled = isSaving || isNexting || !hasNodes

    const handleUndo = () => {
        // if (editor && canUndo) editor.undo()
    }

    const handleRedo = () => {
        // if (editor && canRedo) editor.redo()
    }

    const cleanupDraft = useCallback(async (draftId) => {
        if (!draftId) return
        try {
            const entry = DraftPersistentService.loadOne(draftId)
            if (entry?.previewKey) {
                await DraftPreviewStore.delete(entry.previewKey)
            }
            await DraftImageStore.deleteByDraftId(draftId)
            DraftPersistentService.deleteOne(draftId)
        } catch {
            // Cleanup is best-effort; don't block the main flow.
        }
    }, [])

    const handleSave = async () => {
        setIsSaving(true)
        try {
            const blob = await capturePreviewBlob(VIEW_MODES.SILHOUETTE)
            const url = blob ? URL.createObjectURL(blob) : null
            setSilhouettePreviewUrl(url)
            setIsSaveModalOpen(true)
        } catch (err) {
            toaster({
                type: 'error',
                message: 'Failed to prepare shape preview. Please try again.',
            })
        }
        finally {
            setIsSaving(false)
        }
    }

    const handleSaveConfirm = async (name) => {
        const currentMeta = runtimeRef.current.getDocumentMeta()
        const isUpdate = !!currentMeta?.id

        const effectiveDraftId = currentDraftId ?? CURRENT_SESSION_DRAFT_ID

        const editorState = runtimeRef.current.getState()
        const existingNodeIds = new Set(editorState.nodes.keys())
        for (const [nodeId, node] of editorState.nodes) {
            const src = node.content?.src ?? node.style?.src
            if (!src) continue
            const isBlob = src.startsWith('blob:')
            const isData = src.startsWith('data:')
            if (!isBlob && !isData) continue

            let file = runtime.pendingImageFiles.get(nodeId)
            if (!file) {
                const response = await fetch(src)
                const blob = await response.blob()
                file = new File([blob], 'image.png', {type: blob.type || 'image/png'})
            }

            const result = await ImagePersistenceService.uploadFile(file)
            runtimeRef.current.dispatch(updateNode(nodeId, {content: {src: result.link}}))
            runtime.pendingImageFiles.delete(nodeId)
            await DraftImageStore.delete(effectiveDraftId, nodeId)
            if (isBlob) {
                URL.revokeObjectURL(src)
            }
        }

        for (const nodeId of runtime.pendingImageFiles.keys()) {
            if (!existingNodeIds.has(nodeId)) {
                runtime.pendingImageFiles.delete(nodeId)
            }
        }

        await runtimeRef.current.saveDocument({name, description: ''})

        if (currentDraftId) {
            await cleanupDraft(currentDraftId)
            setCurrentDraftId(null)
        }
        await DraftImageStore.deleteByDraftId(CURRENT_SESSION_DRAFT_ID)

        setSavedShapesRefreshKey((value) => value + 1)

        setSilhouettePreviewUrl((url) => {
            if (url) URL.revokeObjectURL(url)
            return null
        })
        setIsSaveModalOpen(false)

        toaster({
            type: 'success',
            message: isUpdate ? 'Shape updated successfully.' : 'Shape saved successfully.',
            duration: 3000,
        })
    }

    const captureCanvasBlob = async (canvasType = VIEW_MODES.SILHOUETTE, existingCanvas = null) => {
        const rt = runtimeRef.current
        if (!rt) return null

        let canvas = existingCanvas

        if (!canvas) {
            const currentState = rt.getState()
            const bounds = getContentBounds(currentState, {padding: 2})

            const canvasGetters = {
                [VIEW_MODES.SILHOUETTE]: (bounds) =>
                    bounds
                        ? rt.getSilhouetteCanvas(bounds, EXPORT_SILHOUETTE_OPTIONS)
                        : rt.getSilhouetteCanvas({}, EXPORT_SILHOUETTE_OPTIONS),
                [VIEW_MODES.OVERLAY]: (bounds) => (bounds ? rt.getOverlayCanvas(bounds) : rt.getOverlayCanvas()),
            }

            const getter = canvasGetters[canvasType]
            if (!getter) return null

            canvas = getter(bounds)
        }

        if (!canvas) return null

        return new Promise((resolve) => {
            canvas.toBlob(resolve, 'image/png')
        })
    }

    const capturePreviewBlob = (canvasType = VIEW_MODES.SILHOUETTE) => captureCanvasBlob(canvasType)

    const handleNext = async () => {
        if (!canProceedNext || isSaving || isNexting) {
            return
        }

        setIsNexting(true)
        try {
            const currentState = editorEngine.getState()
            const document = serialize(currentState)
            const bounds = getContentBounds(currentState, {padding: 2})

            const blob = await capturePreviewBlob(VIEW_MODES.SILHOUETTE)

            const draftId = currentDraftId || DraftPersistentService.generateId()
            const previewKey = draftId

            if (blob) {
                await DraftPreviewStore.save(previewKey, blob)
            }

            if (!currentDraftId) {
                await DraftImageStore.migrateDraftId(CURRENT_SESSION_DRAFT_ID, draftId)
            }

            DraftPersistentService.saveOne({
                id: draftId,
                document,
                previewKey: blob ? previewKey : '',
                updatedAt: Date.now(),
            })

            // Save mapping payload for Step 2 to consume
            const mappingPayload = {
                draftId,
                document,
                bounds,
            }
            try {
                localStorage.setItem(STORAGE_KEYS.CUSTOM_SHAPE_MAPPING_PAYLOAD, JSON.stringify(mappingPayload))
            } catch {
                // Best-effort: localStorage may be unavailable
            }

            const callbackUrl = searchParams.get('callbackUrl')
            if (callbackUrl) navigate(`/${callbackUrl}`)
        } finally {
            setIsNexting(false)
        }
    }

    const handleSelectDraft = useCallback((draftId) => {
        setCurrentDraftId(draftId)
    }, [])

    const handleSelectShape = useCallback(() => {
        setCurrentDraftId(null)
    }, [])

    useEffect(() => {
        const getListTemplates = async () => {
            const {success, data} = await getListCustomShapeTemplate()
            if (!success) return
        }
        getListTemplates()
    }, [])

    const currentMeta = runtime.getDocumentMeta()

    return (
        <div className="CreateCustomShape">
            <header className="CreateCustomShape__Header">
                <div className="CreateCustomShape__HeaderLeft">
                    <button type="button" className="CreateCustomShape__BackButton" onClick={() => navigate(-1)}>
                        <ChevronLeftIcon className="CreateCustomShape__BackIcon" />
                        <span className="CreateCustomShape__BackLabel">{CustomShapeUIConfig.PRODUCT.BACK_LABEL}</span>
                    </button>

                    <div className="CreateCustomShape__ProductInfo">
                        <div className="CreateCustomShape__ProductTitle">
                            {/* {CustomShapeUIConfig.PRODUCT.TITLE} */}
                            Custom Shape
                        </div>
                    </div>
                </div>

                <div className="CreateCustomShape__HeaderRight">
                    <button
                        type="button"
                        className="CreateCustomShape__SecondaryButton"
                        onClick={handleSave}
                        disabled={isSaveDisabled}
                        aria-label={CustomShapeUIConfig.PRODUCT.PRIMARY_ACTION_LABEL}
                    >
                        {isSaving ? (
                            <>
                                <SpinnerIcon aria-hidden="true" className="CreateCustomShape__Spinner" />
                                Saving...
                            </>
                        ) : (
                            CustomShapeUIConfig.PRODUCT.PRIMARY_ACTION_LABEL
                        )}
                    </button>
                    <button
                        type="button"
                        className="CreateCustomShape__PrimaryButton"
                        onClick={handleNext}
                        disabled={isNextDisabled}
                        aria-label="Next"
                    >
                        {isNexting ? (
                            <>
                                <SpinnerIcon aria-hidden="true" className="CreateCustomShape__Spinner" />
                                Loading...
                            </>
                        ) : (
                            'Next'
                        )}
                    </button>
                </div>
            </header>

            <main className="CreateCustomShape__Body">
                <section className="CreateCustomShape__EditorPanel" aria-label="Editor canvas">
                    <ViewToggle value={viewMode} onChange={setViewMode} />
                    <div className="CreateCustomShape__CanvasWrapper" ref={canvasWrapperRef} style={{position: 'relative'}}>
                        {!hasNodes && <CanvasEmptyState runtime={runtime} draftId={currentDraftId} />}
                        <EditorCanvas
                            runtime={runtime}
                            state={state}
                            width={canvasSize.width}
                            height={canvasSize.height}
                            viewMode={viewMode}
                            enableDetection={true}
                            onFragmentsChange={setFragmentCount}
                            onRemoveBg={onOpenRemoveBg}
                        />
                    </div>

                    <footer className="CreateCustomShape__Footer">
                        <div className="CreateCustomShape__FooterGroup">
                            <button
                                type="button"
                                className="CreateCustomShape__FooterButton"
                                onClick={handleUndo}
                                // disabled={!canUndo}
                                title="Undo"
                                aria-label="Undo"
                            >
                                <UndoIcon />
                            </button>
                            <button
                                type="button"
                                className="CreateCustomShape__FooterButton"
                                onClick={handleRedo}
                                // disabled={!canRedo}
                                title="Redo"
                                aria-label="Redo"
                            >
                                <RedoIcon />
                            </button>
                        </div>

                        <div className="CreateCustomShape__FooterGroup CreateCustomShape__FooterGroup--right">
                            <button
                                type="button"
                                className={`CreateCustomShape__FooterButton ${interactionMode === InteractionMode.PAN ? 'is-active' : ''}`}
                                onClick={toggleMode}
                            >
                                <PalmIcon />
                            </button>
                            <div className="CreateCustomShape__FooterDivider" />
                            <button type="button" className="CreateCustomShape__FooterButton" onClick={handleZoomOut}>
                                <MinusIcon />
                            </button>
                            <span className="CreateCustomShape__ZoomLabel">{Math.round(viewportScale * 100)}%</span>
                            <button type="button" className="CreateCustomShape__FooterButton" onClick={handleZoomIn}>
                                <PlusIcon />
                            </button>
                        </div>
                    </footer>
                </section>

                <Sidebar
                    runtime={runtime}
                    state={state}
                    dispatch={dispatch}
                    currentDraftId={currentDraftId}
                    savedShapesRefreshKey={savedShapesRefreshKey}
                    onSelectDraft={handleSelectDraft}
                    onSelectShape={handleSelectShape}
                    onOpenRemoveBg={onOpenRemoveBg}
                />
            </main>

            <RemoveBgModal
                isOpen={showRemoveBgModal}
                toggle={() => setShowRemoveBgModal(false)}
                imageSrc={removeBgNode?.content?.src ?? removeBgNode?.style?.src ?? null}
                onApply={handleRemoveBgOnApply}
            />
            <SaveShapeModal
                initialData={{
                    name: currentMeta?.name,
                    previewUrl: silhouettePreviewUrl,
                }}
                isOpen={isSaveModalOpen}
                onClose={() => {
                    if (silhouettePreviewUrl) URL.revokeObjectURL(silhouettePreviewUrl)
                    setSilhouettePreviewUrl(null)
                    setIsSaveModalOpen(false)
                }}
                onSuccess={handleSaveConfirm}
                onError={(err) =>
                    toaster({
                        type: 'error',
                        message: err?.message || 'Failed to save shape.',
                        duration: 5000,
                    })
                }
            />
        </div>
    )
}

export default CreateCustomShape

export const NavigateWrapper = () => {
    const [searchParams] = useSearchParams()
    const callback = searchParams.get('callbackUrl')
    if (!callback) {
        return <Navigate to="/" replace />
    }
    return <Outlet />
}
